import { Inject, Injectable, PLATFORM_ID } from '@angular/core';

import { LegacyToken } from '@service/legacy/model/legacy-token';
import { LegacyOnlyBrowser } from '@service/legacy/legacy-only-browser.decorator';
import { LegacyRouteHelper } from '../../ui/legacy/helper/legacy-route-paths';

import { v4 as uuid } from 'uuid';

@LegacyOnlyBrowser()
@Injectable({
  providedIn: 'root',
})
export class LegacySafeStorageService {
  private readonly legacyUserName = 'legacy_name';
  private readonly legacyDeviceId = 'legacy_device_id';
  private readonly legacyToken = 'legacy_token';
  private readonly legacyCanApplySettle = 'legacy_can_apply_settle';
  private readonly legacyTrueValue = 't';
  private readonly legacyRedirectUrl = 'legacy_redirect_url';

  static PLATFORM_ID: object;

  constructor(@Inject(PLATFORM_ID) platformId: object) {
    LegacySafeStorageService.PLATFORM_ID = platformId;
  }

  getLocalItem(key: string): string | undefined {
    try {
      return localStorage?.getItem(key) ?? undefined;
    } catch {
      // ignore
    }
    return undefined;
  }

  setLocalItem(key: string, value: string): void {
    try {
      localStorage?.setItem(key, value);
    } catch {
      // ignore
    }
  }

  removeLocalItem(key: string): void {
    try {
      localStorage?.removeItem(key);
    } catch {
      // ignore
    }
  }

  setRedirectUrl(url: string): void {
    this.setLocalItem(this.legacyRedirectUrl, url);
  }

  getRedirectUrl(): string | undefined {
    const url = this.getLocalItem(this.legacyRedirectUrl);
    if (url === LegacyRouteHelper.legacyRefundAccount()) {
      // 從退款帳戶, 嘗試其它帳號登入後, 導回不用再次驗證
      this.setCanApplySettle(true);
    }
    this.removeLocalItem(this.legacyRedirectUrl);
    return url;
  }

  getDeviceId(): string {
    let deviceId = this.getLocalItem(this.legacyDeviceId);
    if (!deviceId) {
      deviceId = uuid();
      this.setLocalItem(this.legacyDeviceId, deviceId);
    }
    return deviceId;
  }

  setDeviceId(id: string): void {
    this.setLocalItem(this.legacyDeviceId, id);
  }

  getLegacyToken(): LegacyToken | undefined {
    const tokenStr = this.getLocalItem(this.legacyToken);
    if (!tokenStr) {
      return undefined;
    }
    return JSON.parse(tokenStr);
  }

  setLegacyToken(token?: LegacyToken): void {
    if (!token || !token.accessToken || !token.refreshToken) {
      return;
    }
    this.setLocalItem(this.legacyToken, JSON.stringify(token));
  }

  clearLegacyToken(): void {
    this.removeLocalItem(this.legacyToken);
  }

  setUserName(name: string): void {
    this.removeLocalItem(this.legacyUserName);
    this.setLocalItem(this.legacyUserName, name);
  }

  getUserName(): string {
    return this.getLocalItem(this.legacyUserName) || 'lativ會員';
  }

  isLogin(): boolean {
    return !!this.getLegacyToken()?.accessToken;
  }

  setCanApplySettle(isVerify: boolean): void {
    this.setLocalItem(this.legacyCanApplySettle, isVerify ? this.legacyTrueValue : '');
  }

  getCanApplySettle(): boolean | undefined {
    return this.getLocalItem(this.legacyCanApplySettle) === this.legacyTrueValue;
  }

  clearLegacyAll(): void {
    this.removeLocalItem(this.legacyDeviceId);
    this.removeLocalItem(this.legacyToken);
    this.removeLocalItem(this.legacyCanApplySettle);
  }
}
